import type { Portal } from './types/auth.types';

/**
 * A dónde va una persona apenas se sabe a qué empresas puede entrar.
 *
 * - `sin-empresas` — no tiene ninguna membresía. No hay nada que canjear y el
 *   ingreso ya no abre sesión por su cuenta, así que no hay a dónde mandarla:
 *   corresponde decírselo.
 * - `elegir` — tiene varias; la pantalla de portales.
 * - `entrar` — tiene una sola; entra derecho, porque quedarse mirando un menú
 *   de un solo botón es una traba y no una elección.
 */
export type DestinoTrasIngresar =
  | { tipo: 'sin-empresas' }
  | { tipo: 'elegir' }
  | { tipo: 'entrar'; slug: string };

/**
 * La decisión, una sola vez y en un solo lugar.
 *
 * 🔴 Por qué existe. Vivía escrita dos veces —en `login-form.tsx` y en
 * `login-page.tsx`— y eso se había anotado como duplicación cosmética. Dejó de
 * ser cosmética cuando el corte del ingreso volvió **correcta una copia y
 * equivocada la otra**: la del formulario aprendió a canjear el portal antes de
 * recargar, y la de la pantalla se quedó redirigiendo a `/dashboard`, una ruta
 * sin prefijo de empresa que el `basename` ya había invalidado. Un duplicado
 * desactualizado es otra cosa que un duplicado.
 *
 * Lo que NO decide: qué hacer con cada destino. El formulario tiene que canjear
 * antes de recargar (todavía no hay sesión); la pantalla ya tiene una y deja que
 * el guardián de rutas reconcilie. Son efectos distintos por un motivo real, así
 * que se comparte la decisión y no la acción.
 */
export function destinoTrasIngresar(portales: Portal[]): DestinoTrasIngresar {
  if (portales.length === 0) return { tipo: 'sin-empresas' };
  if (portales.length > 1) return { tipo: 'elegir' };
  return { tipo: 'entrar', slug: portales[0].slug };
}
